import { supabase } from '@/config/supabase';
import { env } from '@/config/env';
import type { AuthError, User, Session } from '@supabase/supabase-js';

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
  recaptchaToken?: string; // Google reCAPTCHA v3 token for bot prevention
}

export interface RegisterCredentials {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string; // OPTIONAL: Can be added later in profile settings
  gender: 'male' | 'female' | 'other';
  dateOfBirth: string;
  locale: 'en' | 'ar'; // Auto-detected from browser, passed for profile creation
  recaptchaToken: string; // Google reCAPTCHA v3 token for bot prevention
}

export interface AuthResponse {
  user: User | null;
  session?: Session | null;
  error: AuthError | null;
}

export interface AuthErrorResponse {
  user: null;
  error: {
    message: string;
    code?: string;
  };
}

/**
 * Generic error messages - aligned with Edge Function
 * These prevent information leakage about account existence
 */
const GENERIC_ERRORS = {
  signup: 'Unable to create account. Please try again.',
  login: 'Invalid credentials. Please check your email and password.',
  passwordReset: 'If an account exists, a reset link will be sent.',
  rateLimit: 'Too many attempts. Please try again later.',
  serverError: 'An unexpected error occurred. Please try again.',
} as const;

/**
 * Call the auth-wrapper Edge Function
 */
async function callAuthWrapper(
  action: 'signup' | 'login' | 'password-reset',
  email: string,
  password?: string,
  metadata?: Record<string, any>
): Promise<{ user?: User; session?: Session; error?: string }> {
  try {
    const response = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/auth-wrapper`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        action,
        email,
        password,
        metadata,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Auth wrapper error response:', data);
      return { error: data.error || GENERIC_ERRORS.serverError };
    }

    return data;
  } catch (error) {
    console.error('Auth wrapper call failed:', error);
    return { error: GENERIC_ERRORS.serverError };
  }
}

/**
 * Authentication service
 * Handles all Supabase auth operations
 */
export const authService = {
  /**
   * Sign in with email and password
   *
   * SECURITY FEATURES:
   * - Rate limiting: 5 attempts per 15 minutes per email (enforced by Edge Function)
   * - Generic error messages: Never reveals if email exists
   * - Audit logging: All attempts logged to auth_audit_log
   *
   * Session persistence:
   * - Sessions automatically persist to localStorage
   * - rememberMe flag available for future custom session duration logic
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const { email, password, recaptchaToken } = credentials;

    // Call secure auth wrapper Edge Function
    const result = await callAuthWrapper('login', email, password, {
      recaptcha_token: recaptchaToken,
    });

    if (result.error) {
      return {
        user: null,
        error: {
          message: result.error,
          name: 'AuthError',
          status: 401,
        } as AuthError,
      };
    }

    // If login successful, establish session locally
    if (result.session) {
      await supabase.auth.setSession(result.session);
    }

    return {
      user: result.user || null,
      session: result.session,
      error: null,
    };
  },

  /**
   * Register new user with profile creation
   *
   * SECURITY FEATURES:
   * - Rate limiting: 3 attempts per hour per IP (enforced by Edge Function)
   * - Generic error messages: Never reveals why signup failed
   * - Audit logging: All attempts logged to auth_audit_log
   *
   * Profile is automatically created via database trigger (handle_new_user)
   * Email verification is DISABLED - users are signed in immediately
   */
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    try {
      // Prepare metadata for Edge Function
      const metadata = {
        first_name: credentials.firstName,
        last_name: credentials.lastName,
        ...(credentials.phoneNumber && { phone_number: credentials.phoneNumber }), // OPTIONAL: Only include if provided
        gender: credentials.gender,
        date_of_birth: credentials.dateOfBirth,
        locale: credentials.locale,
      };

      // Call secure auth wrapper Edge Function
      const result = await callAuthWrapper(
        'signup',
        credentials.email,
        credentials.password,
        {
          ...metadata,
          recaptcha_token: credentials.recaptchaToken,
        }
      );

      if (result.error) {
        return {
          user: null,
          error: {
            message: result.error,
            name: 'SignUpError',
            status: 400,
          } as AuthError,
        };
      }

      // User created successfully
      // The Edge Function creates the auth user, and database trigger creates profile
      // Now we need to sign in the user to establish a session
      if (result.user) {
        // Sign in immediately after signup (no email verification)
        const { data, error } = await supabase.auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password,
        });

        if (error) {
          // User was created but auto-login failed
          // Return success but ask them to login manually
          return {
            user: result.user,
            error: {
              message: 'Account created. Please log in.',
              name: 'AutoLoginFailed',
              status: 200,
            } as AuthError,
          };
        }

        return {
          user: data.user,
          session: data.session,
          error: null,
        };
      }

      return {
        user: null,
        error: {
          message: GENERIC_ERRORS.signup,
          name: 'SignUpError',
          status: 500,
        } as AuthError,
      };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        user: null,
        error: {
          message: GENERIC_ERRORS.serverError,
          name: 'UnknownError',
          status: 500,
        } as AuthError,
      };
    }
  },

  /**
   * Sign out current user
   */
  async logout(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User | null> {
    const { data } = await supabase.auth.getUser();
    return data.user;
  },

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user !== null;
  },

  /**
   * Send password reset email
   *
   * SECURITY FEATURES:
   * - Rate limiting: 3 attempts per hour per email (enforced by Edge Function)
   * - Generic success message: Never reveals if email exists
   * - Audit logging: All requests logged to auth_audit_log
   */
  async sendPasswordResetEmail(email: string): Promise<{ error: AuthError | null }> {
    // Call secure auth wrapper Edge Function
    const result = await callAuthWrapper('password-reset', email);

    // Always return success - never reveal if email exists
    // Generic message is returned by Edge Function
    if (result.error && result.error !== GENERIC_ERRORS.passwordReset) {
      // Only return error if it's a rate limit or server error
      return {
        error: {
          message: result.error,
          name: 'PasswordResetError',
          status: 400,
        } as AuthError,
      };
    }

    return { error: null };
  },

  /**
   * Update password (for authenticated users)
   */
  async updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  },
};
